// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Box as Form, toast } from '@node-space/ui-components'
import { AddBeneficiaryContextProvider } from 'contexts/addBeneficiaryContext'
import {
  useAddBeneficiaryMutationChallengeRequest,
  useAddBeneficiaryMutationPreRequest,
} from 'hooks/mutations/useBeneficiariesMutation'
import { useNgBankAccountValidationMutation } from 'hooks/mutations/useNgBankAccountValidationMutation'
import { TransferDestinationSelector } from 'pages/Contacts/components/FiatInstrument/TransferDestinationSelector'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import {
  AddBeneficiaryBankDetailsPayload,
  AddBeneficiaryBasicPayload,
  AddBeneficiaryFormPayload,
  AddBeneficiaryFormPayloadErrors,
  AddBeneficiaryPostResponse,
  AddIntermediaryBankDetailsPayload,
  BaseErrorResponse,
  BeneficiaryDetailsCurrency,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
  BeneficiaryType,
  ChallengeRequest,
  PaymentMethod,
} from 'types/beneficiaries'
import { Error } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import {
  orchestrateAddBeneficiaryPayload,
  setInternationalBeneficiaryError,
  setLocalBeneficiaryError,
} from 'utils/beneficiaries'
import track from 'utils/tracker'
import { sanitizeValues } from 'utils/utils'
import { ERROR_MESSAGES } from '../constants'
import { AddBeneficiaryConfirm } from './components/AddBeneficiaryConfirm'
import { AddBeneficiaryForm } from './components/AddBeneficiaryForm'
import { BeneficiaryBankDetails } from './components/BeneficiaryBankDetails/BeneficiaryBankDetails'
import { IntermediaryBankDetails } from './components/IntermediaryBankDetails/IntermediaryBankDetails'

export const ModalStep = {
  TRANSFER_TYPE: 'TRANSFER_TYPE',
  ADD_BENEFICIARY: 'ADD_BENEFICIARY',
  INTERNATIONAL_BANK_DETAILS: 'INTERNATIONAL_BANK_DETAILS',
  INTERMEDIARY_BANK_DETAILS: 'INTERMEDIARY_BANK_DETAILS',
  CONFIRMATION: 'CONFIRMATION',
  CLOSE_MODAL: 'CLOSE_MODAL',
} as const

export type ModalStep = (typeof ModalStep)[keyof typeof ModalStep]

export interface AddBeneficiaryProps {
  handleCloseModal: () => void
  handleOnSuccessCloseModal: () => void
  addBeneficiaryStep: ModalStep
  setAddBeneficiaryStep: (step: ModalStep) => void
  beneficiaryFormState: AddBeneficiaryFormPayload
  setBeneficiaryFormState: (state: AddBeneficiaryFormPayload) => void
}

export const AddBeneficiary = ({
  handleCloseModal,
  handleOnSuccessCloseModal,
  addBeneficiaryStep,
  setAddBeneficiaryStep,
  beneficiaryFormState,
  setBeneficiaryFormState,
}: AddBeneficiaryProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [beneficiaryIbanValidationState, setBeneficiaryIbanValidationState] =
    useState<AddBeneficiaryBankDetailsPayload>({} as AddBeneficiaryBankDetailsPayload)
  const [beneficiaryBicValidationState, setBeneficiaryBicValidationState] = useState<string>(
    {} as string
  )
  const [beneficiaryFormErrors, setBeneficiaryFormErrors] =
    useState<AddBeneficiaryFormPayloadErrors>(null)
  const [submitPreRequest, setSubmitPreRequest] = useState(false)
  const [submitBeneficiary, setSubmitBeneficiary] = useState(false)
  const [validatingBankAccount, setValidatingBankAccount] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setBeneficiaryFormState({
      ...beneficiaryFormState,
      beneficiaryEntityType:
        beneficiaryFormState?.beneficiaryEntityType ?? BeneficiaryEntityType.BUSINESS,
      beneficiaryType: beneficiaryFormState?.beneficiaryType ?? BeneficiaryType.THIRD_PARTY,
      transferDestination:
        beneficiaryFormState?.transferDestination ?? BeneficiaryTransferDestination.LOCAL,
    })
  }, [])

  const transferDestination = useMemo(() => {
    return beneficiaryFormState?.transferDestination
  }, [beneficiaryFormState?.transferDestination])

  const { mutate: mutatePreRequest, isPending: isLoadingPreRequest } =
    useAddBeneficiaryMutationPreRequest()

  const { mutate: mutatePostRequest, isPending: isLoadingPostRequest } =
    useAddBeneficiaryMutationChallengeRequest()

  const { mutate: validateNgBankAccount } = useNgBankAccountValidationMutation()

  const isSubmitting = useMemo(() => {
    return validatingBankAccount || isLoadingPreRequest || loading
  }, [validatingBankAccount, isLoadingPreRequest, loading])

  useEffect(() => {
    submitPreRequest && onSubmitPreRequest()
  }, [submitPreRequest])

  useEffect(() => {
    submitBeneficiary && onSubmitPostRequest()
  }, [submitBeneficiary])

  const onSubmitPreRequest = async () => {
    const payload = orchestrateAddBeneficiaryPayload(
      sanitizeValues(beneficiaryFormState) as AddBeneficiaryFormPayload
    )

    const mutatePreRequestErrorHandler = (error: AddBeneficiaryPostResponse) => {
      if (error?.status === 428) {
        setBeneficiaryFormState({
          ...beneficiaryFormState,
          challengeCode: error?.data?.challenge?.value,
          mfaCallbackUrl: error?.data?.callbackUrl,
        })

        setAddBeneficiaryStep(ModalStep.CONFIRMATION)
      }

      if (error.status === 400) {
        if (
          beneficiaryFormState?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
        ) {
          const errorMessage = error?.data?.details?.errors?.requestBody[0] || ''
          const isIntermediaryBankError = errorMessage.includes(
            ERROR_MESSAGES.INVALID_INTERMEDIARY_BANK_MESSAGE
          )
          const isNameError = errorMessage.includes(ERROR_MESSAGES.INVALID_BENEFICIARY_NAME_MESSAGE)
          const errorFields = isIntermediaryBankError
            ? ['intermediaryBic']
            : isNameError
              ? ['alias']
              : ['intlIban', 'bicSwiftCode']

          setAddBeneficiaryStep(
            isIntermediaryBankError
              ? ModalStep.INTERMEDIARY_BANK_DETAILS
              : isNameError
                ? ModalStep.ADD_BENEFICIARY
                : ModalStep.INTERNATIONAL_BANK_DETAILS
          )
          setInternationalBeneficiaryError(
            errorMessage,
            errorFields,
            beneficiaryFormErrors,
            setBeneficiaryFormErrors
          )
        } else {
          setAddBeneficiaryStep(ModalStep.ADD_BENEFICIARY)
          setLocalBeneficiaryError(
            beneficiaryFormState.currencyCode,
            error,
            beneficiaryFormErrors,
            setBeneficiaryFormErrors,
            beneficiaryFormState?.beneficiaryPaymentMethod as PaymentMethod
          )
        }
      }
    }

    if (beneficiaryFormState?.currencyCode === BeneficiaryDetailsCurrency.NGN) {
      setValidatingBankAccount(true)
      validateNgBankAccount(
        {
          accountNumber: beneficiaryFormState?.ngnAccountNumber,
          bankCode: beneficiaryFormState?.bankCode,
        },
        {
          onSuccess: () => {
            mutatePreRequest(payload, {
              onError: mutatePreRequestErrorHandler,
            })
            setValidatingBankAccount(false)
          },
          onError: (err: BaseErrorResponse) => {
            const errors = err?.data?.errorList?.reduce((fieldErrors: {}, fieldError: Error) => {
              if (fieldError?.parameter && fieldError?.message) {
                return {
                  ...fieldErrors,
                  [fieldError?.parameter]: { message: fieldError?.message },
                }
              }

              return fieldErrors
            }, [])
            setBeneficiaryFormErrors({ ...beneficiaryFormErrors, ...errors })
            setValidatingBankAccount(false)
          },
        }
      )
    } else {
      mutatePreRequest(payload, {
        onError: mutatePreRequestErrorHandler,
      })
    }
    setLoading(false)
    setSubmitPreRequest(false)
  }

  const onSubmitPostRequest = () => {
    const { password, challengeCode, mfaCallbackUrl } = beneficiaryFormState
    const payload: ChallengeRequest = {
      challenge: challengeCode,
      tokenResponse: password,
      mfaCallbackUrl,
    }

    mutatePostRequest(payload, {
      onSuccess: async () => {
        handleOnSuccessCloseModal()
        await queryClient.invalidateQueries({ queryKey: [reactQueryName.BENEFICIARIES] })
        toast({
          header: t('manageBeneficiaries.beneficiarySuccess'),
          variant: 'success',
        })
        setSubmitBeneficiary(false)
        track.Amp.track(AmplitudeEvent.EOF_ADD_BENEFICIARY_SUCCESS, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.VIEW,
        })
      },
      onError: (error: BaseErrorResponse) => {
        const isAliasError = error?.data?.message?.includes('already in use for this account')

        if (error.status === 403) {
          setBeneficiaryFormErrors({ ...beneficiaryFormErrors, password: t('incorrectOtpCode') })
        } else if (isAliasError) {
          setBeneficiaryFormErrors({
            ...beneficiaryFormErrors,
            alias: { message: t('aliasError') },
          })
          setAddBeneficiaryStep(ModalStep.ADD_BENEFICIARY)
        } else {
          setBeneficiaryFormErrors({
            ...beneficiaryFormErrors,
            password: { message: error?.data?.message },
          })
        }

        setSubmitBeneficiary(false)
      },
    })
  }

  const submit = (password: string) => {
    setBeneficiaryFormState({ ...beneficiaryFormState, password })

    setSubmitBeneficiary(true)
  }

  const appendTransferDestination = async (transferDestination: BeneficiaryTransferDestination) => {
    setBeneficiaryFormState({ ...beneficiaryFormState, transferDestination })
    setAddBeneficiaryStep(ModalStep.ADD_BENEFICIARY)
  }

  const appendBeneficiaryDetails = async (beneficiaryDetails: AddBeneficiaryBasicPayload) => {
    setBeneficiaryFormState({ ...beneficiaryFormState, ...beneficiaryDetails })
    if (
      beneficiaryFormState?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
    ) {
      setAddBeneficiaryStep(ModalStep.INTERNATIONAL_BANK_DETAILS)
    } else {
      setSubmitPreRequest(true)
    }
  }

  const appendInternationBankDetails = async (bankDetails: AddBeneficiaryBankDetailsPayload) => {
    setBeneficiaryFormState({ ...beneficiaryFormState, ...bankDetails })
    if (
      beneficiaryFormState?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
    ) {
      setAddBeneficiaryStep(ModalStep.INTERMEDIARY_BANK_DETAILS)
    } else {
      setSubmitPreRequest(true)
    }
  }

  const appendIntermediaryBankDetails = async (
    bankDetails: AddIntermediaryBankDetailsPayload,
    skipIntermediaryBankScreen: boolean
  ) => {
    setBeneficiaryFormState({
      ...beneficiaryFormState,
      ...bankDetails,
      skipIntermediaryBankScreen,
    })

    setSubmitPreRequest(true)
  }

  const onBackHandler = () => {
    setBeneficiaryFormErrors({} as AddBeneficiaryFormPayloadErrors)

    switch (addBeneficiaryStep) {
      case ModalStep.TRANSFER_TYPE:
        handleCloseModal()
        break
      case ModalStep.ADD_BENEFICIARY:
        setAddBeneficiaryStep(ModalStep.TRANSFER_TYPE)
        break
      case ModalStep.INTERNATIONAL_BANK_DETAILS:
        setAddBeneficiaryStep(ModalStep.ADD_BENEFICIARY)
        break
      case ModalStep.INTERMEDIARY_BANK_DETAILS:
        setAddBeneficiaryStep(ModalStep.INTERNATIONAL_BANK_DETAILS)
        break
      case ModalStep.CONFIRMATION:
        if (beneficiaryFormState?.transferDestination === BeneficiaryTransferDestination.LOCAL) {
          setAddBeneficiaryStep(ModalStep.ADD_BENEFICIARY)
        } else {
          setAddBeneficiaryStep(ModalStep.INTERMEDIARY_BANK_DETAILS)
        }
        break
      default:
        break
    }
  }

  const skipStep = (nextStep: ModalStep) => {
    setAddBeneficiaryStep(nextStep)
    setSubmitPreRequest(true)
    setBeneficiaryFormState({ ...beneficiaryFormState, skipIntermediaryBankScreen: true })
  }

  const renderStep = () => {
    if (addBeneficiaryStep === ModalStep.TRANSFER_TYPE) {
      return <TransferDestinationSelector onTransferDestinationClick={appendTransferDestination} />
    }

    if (addBeneficiaryStep === ModalStep.ADD_BENEFICIARY) {
      return (
        <AddBeneficiaryForm
          transferDestination={transferDestination}
          isSubmitting={isSubmitting}
          onBack={onBackHandler}
          onContinue={beneficiaryDetails => appendBeneficiaryDetails(beneficiaryDetails)}
        />
      )
    }

    if (addBeneficiaryStep === ModalStep.INTERNATIONAL_BANK_DETAILS) {
      return (
        <BeneficiaryBankDetails
          isSubmitting={isLoadingPreRequest || loading}
          onBack={onBackHandler}
          onContinue={bankDetails => appendInternationBankDetails(bankDetails)}
        />
      )
    }

    if (addBeneficiaryStep === ModalStep.INTERMEDIARY_BANK_DETAILS) {
      return (
        <IntermediaryBankDetails
          isSubmitting={isSubmitting}
          onBack={onBackHandler}
          onContinue={(bankDetails, skipDetails) =>
            appendIntermediaryBankDetails(bankDetails, skipDetails)
          }
          skipStep={skipStep}
        />
      )
    }

    if (addBeneficiaryStep === ModalStep.CONFIRMATION) {
      return (
        <AddBeneficiaryConfirm
          error={!!beneficiaryFormErrors?.password}
          isLoading={isLoadingPostRequest}
          onContinue={password => submit(password)}
          setStep={setAddBeneficiaryStep}
        />
      )
    }
  }

  return (
    <AddBeneficiaryContextProvider
      value={{
        beneficiaryFormState,
        setBeneficiaryFormState,
        beneficiaryIbanValidationState,
        setBeneficiaryIbanValidationState,
        beneficiaryBicValidationState,
        setBeneficiaryBicValidationState,
        beneficiaryFormErrors,
        setBeneficiaryFormErrors,
      }}
    >
      <Form tag="form">{renderStep()}</Form>
    </AddBeneficiaryContextProvider>
  )
}
